import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Badge, Button, Empty, List, Popover, Spin, Typography } from "antd";
import { BellOutlined, CheckOutlined } from "@ant-design/icons";
import { notificacionesApi, Notificacion } from "../services/notificaciones.api";
import { useWebSocket } from "../context/WebSocketContext";

const { Text } = Typography;

/** Ancho del panel de avisos en escritorio. */
const ANCHO_PANEL = 360;
/** Lo que el Popover suma alrededor del contenido (relleno interior) y un margen con el borde izquierdo. */
const RESERVA_LATERAL = 40;
const ANCHO_MINIMO = 220;

/**
 * El Popover se alinea por la derecha de la campana; en un celular, 360 px de contenido más su relleno se salen por
 * la izquierda de la pantalla (hallado en celular real emulado, E2E avisos-ventana). Se acota al espacio que hay a la
 * izquierda del borde derecho de la campana. Sin medida (jsdom, campana fuera de pantalla) se usa el ancho completo.
 */
export const anchoPanelAvisos = (bordeDerechoCampana: number): number =>
  bordeDerechoCampana > 0
    ? Math.max(ANCHO_MINIMO, Math.min(ANCHO_PANEL, Math.floor(bordeDerechoCampana - RESERVA_LATERAL)))
    : ANCHO_PANEL;

/** Destino de un aviso: solo rutas internas de la SPA (`/…`), nunca un enlace externo. */
export const rutaDeNotificacion = (n: Notificacion): string | null => {
  const ruta = n.datos?.ruta;
  return typeof ruta === "string" && ruta.startsWith("/") && !ruta.startsWith("//") ? ruta : null;
};

/** «hace 5 min», «hace 2 h», «ayer», o la fecha. Hora del dispositivo, solo para mostrar. */
export const cuandoNotificacion = (iso: string, ahora: Date = new Date()): string => {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const min = Math.max(0, Math.round((ahora.getTime() - t) / 60_000));
  if (min < 1) return "ahora";
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.round(h / 24);
  return d === 1 ? "ayer" : `hace ${d} días`;
};

/**
 * Campana de avisos de la aseadora (header). Badge con el número de avisos sin leer + lista de los
 * más recientes. Tocar un aviso lo marca como leído y lleva a su destino (para «N aseos nuevos
 * disponibles para tomar en <sector>»: /aseos, pestaña «Aseos abiertos», filtrada por sector).
 * Tiempo real vía WebSocket `NOTIFICACION_NUEVA`, con sondeo de respaldo cada minuto.
 *
 * NO es la campana de alertas del administrador (`AlertsBell`): esa muestra alertas del sistema;
 * esta, avisos personales. Solo se monta para el rol aseador (ver Header). Round 2026-10-28.
 */
const NotificacionesBell: React.FC = () => {
  const navigate = useNavigate();
  const { socket } = useWebSocket();
  const [noLeidas, setNoLeidas] = useState(0);
  const [open, setOpen] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [items, setItems] = useState<Notificacion[]>([]);
  const [ancho, setAncho] = useState(ANCHO_PANEL);
  /** La última carga de la lista falló: se conserva lo que había y se avisa (round 2026-10-28). */
  const [errorLista, setErrorLista] = useState(false);

  const refrescarContador = useCallback(async () => {
    // Un fallo de red devuelve null: el número de la campana se queda como estaba (antes pasaba a 0).
    const n = await notificacionesApi.contador();
    if (n !== null) setNoLeidas(n);
  }, []);

  const cargarLista = useCallback(async () => {
    setCargando(true);
    const res = await notificacionesApi.listar(20);
    if (res) {
      setItems(res.data);
      setNoLeidas(res.noLeidas);
      setErrorLista(false);
    } else {
      // No se pudo actualizar: ni se vacía la lista ni se apaga el badge; el panel lo dice.
      setErrorLista(true);
    }
    setCargando(false);
  }, []);

  // Carga inicial + sondeo de respaldo (por si se pierde un mensaje del WebSocket).
  useEffect(() => {
    void refrescarContador();
    const id = window.setInterval(() => void refrescarContador(), 60_000);
    return () => window.clearInterval(id);
  }, [refrescarContador]);

  // Tiempo real: un aviso nuevo (o fusionado) actualiza el contador y, si el panel está abierto,
  // la lista.
  const abiertoRef = useRef(open);
  abiertoRef.current = open;
  useEffect(() => {
    if (!socket) return;
    const alMensaje = (e: MessageEvent) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg?.type !== "NOTIFICACION_NUEVA") return;
        void refrescarContador();
        if (abiertoRef.current) void cargarLista();
      } catch {
        /* mensaje que no es JSON: ignorar */
      }
    };
    socket.addEventListener("message", alMensaje);
    return () => socket.removeEventListener("message", alMensaje);
  }, [socket, refrescarContador, cargarLista]);

  const alAbrir = (v: boolean) => {
    setOpen(v);
    if (v) {
      const campana = document.querySelector('[data-testid="notificaciones-campana"]');
      setAncho(anchoPanelAvisos(campana ? campana.getBoundingClientRect().right : 0));
      void cargarLista();
    }
  };

  const abrirAviso = async (n: Notificacion) => {
    if (!n.leida) {
      const restantes = await notificacionesApi.marcarLeida(n.id);
      // Solo se marca como leído aquí si el servidor lo confirmó: con un fallo de red el aviso sigue sin
      // leer (en la pantalla y en el servidor) en vez de aparentar lo contrario hasta la próxima carga.
      if (restantes != null) {
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, leida: true } : x)));
        setNoLeidas(restantes);
      }
    }
    const ruta = rutaDeNotificacion(n);
    if (ruta) {
      setOpen(false);
      navigate(ruta);
    }
  };

  const marcarTodas = async () => {
    if (await notificacionesApi.marcarTodasLeidas()) {
      setItems((prev) => prev.map((x) => ({ ...x, leida: true })));
      setNoLeidas(0);
    }
  };

  const panel = (
    <div style={{ width: ancho, maxWidth: "90vw" }} data-testid="notificaciones-panel">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <Text strong>Avisos</Text>
        <Button
          type="text"
          size="small"
          icon={<CheckOutlined />}
          disabled={noLeidas === 0}
          onClick={() => void marcarTodas()}
          data-testid="notificaciones-leer-todas"
        >
          Marcar todos como leídos
        </Button>
      </div>

      {errorLista && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 8 }}
          data-testid="notificaciones-error"
          message="No se pudo actualizar"
          description={items.length > 0 ? "Se muestran los últimos avisos que se pudieron cargar." : undefined}
          action={
            <Button size="small" type="link" onClick={() => void cargarLista()} disabled={cargando}>
              Reintentar
            </Button>
          }
        />
      )}

      {cargando && items.length === 0 ? (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin />
        </div>
      ) : items.length === 0 ? (
        // Con la carga fallida no se afirma «No tienes avisos»: no se sabe.
        errorLista ? null : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No tienes avisos"
            style={{ padding: "12px 0" }}
          />
        )
      ) : (
        <List
          size="small"
          dataSource={items}
          style={{ maxHeight: 360, overflowY: "auto" }}
          renderItem={(n) => (
            <List.Item
              data-testid="notificacion-item"
              data-leida={n.leida ? "1" : "0"}
              onClick={() => void abrirAviso(n)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  void abrirAviso(n);
                }
              }}
              role="button"
              tabIndex={0}
              style={{
                display: "block",
                padding: "10px 8px",
                cursor: "pointer",
                background: n.leida ? "transparent" : "rgba(15, 118, 110, 0.07)",
                // 44 px mínimo en táctil
                minHeight: 44,
              }}
            >
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                {!n.leida && (
                  <span
                    aria-label="Sin leer"
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "#0F766E",
                      marginTop: 6,
                      flex: "0 0 auto",
                    }}
                  />
                )}
                <div style={{ minWidth: 0 }}>
                  <Text strong={!n.leida} style={{ fontSize: 13, display: "block" }}>
                    {n.mensaje || n.titulo}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {cuandoNotificacion(n.actualizada_en || n.created_at)}
                  </Text>
                </div>
              </div>
            </List.Item>
          )}
        />
      )}
    </div>
  );

  return (
    <Popover
      content={panel}
      trigger="click"
      placement="bottomRight"
      open={open}
      onOpenChange={alAbrir}
    >
      <Badge count={noLeidas} size="small" overflowCount={99}>
        <Button
          type="text"
          shape="circle"
          aria-label={noLeidas > 0 ? `Avisos, ${noLeidas} sin leer` : "Avisos"}
          data-testid="notificaciones-campana"
          icon={<BellOutlined style={{ fontSize: 18 }} />}
        />
      </Badge>
    </Popover>
  );
};

export default NotificacionesBell;
